import { theme, type ThemeConfig } from "antd";
import type { ThemeMode } from "./mode";

const FONT_FAMILY = "'Rubik', 'PT Sans', sans-serif";

/**
 * Ported from ubc-admin (vendingsystem/services/admin)
 * `src/providers/theme/index.tsx` so both admins render antd identically —
 * same neutrals, control heights, radii and per-component sizing.
 *
 * The one deliberate difference is the brand: TVMS keeps its red primary
 * instead of ubc's #435FE8 blue. Because primary is then the same hue as
 * danger, danger buttons are given a light tint in styles/button.css rather
 * than ubc's solid fill, so "confirm" and "destroy" remain distinguishable.
 */
const baseToken: ThemeConfig["token"] = {
  colorPrimary: "#c81e3a",
  borderRadius: 8,
  colorText: "#101828",
  colorBorder: "#EAECF0",
  controlHeight: 36,
  colorTextPlaceholder: "#667085",
  colorBgContainerDisabled: "#F9FAFB",
  colorTextDisabled: "#667085",
  colorSuccess: "#027A48",
  colorSuccessHover: "#027A48",
  colorSuccessBg: "#ECFDF3",
  colorWarning: "#F79009",
  colorError: "#D92D20",
  colorInfo: "#c81e3a",
  colorTextBase: "#101828",
  colorTextSecondary: "#667085",
  colorTextTertiary: "#999999",
  colorTextQuaternary: "#CCCCCC",
  fontFamily: FONT_FAMILY,
  wireframe: false,
};

const lightToken: ThemeConfig["token"] = {
  ...baseToken,
  colorBgLayout: "#F9FAFB",
  colorBgContainer: "#ffffff",
  colorBorderSecondary: "#EAECF0",
};

// ubc-admin ships light only. These keep TVMS's existing dark mode working
// against the same palette instead of reverting to the old red theme.
const darkToken: ThemeConfig["token"] = {
  ...baseToken,
  // See tailwind.css: #e84360 left the white label on a primary button at
  // 3.89:1. This keeps antd and the Tailwind ramp on the same brand shade.
  colorPrimary: "#d92d46",
  colorInfo: "#d92d46",
  // Aligned with the dark ramp in tailwind.css. These used a neutral grey
  // (#161616/#0a0a0a/#2a2a2a) while the Tailwind side is blue-tinted, so an
  // antd surface such as a table sat visibly off-hue on a Tailwind card.
  colorText: "#f2f4f7", // gray-900
  colorTextBase: "#f2f4f7",
  colorTextSecondary: "#9aa3b2", // gray-500
  colorBgLayout: "#16181f", // gray-100
  colorBgContainer: "#1c1f27", // surface
  colorBgElevated: "#1c1f27",
  colorBorder: "#262932", // gray-200
  colorBorderSecondary: "#262932",
};

/** The brand red, at the shade each theme can actually carry. */
const brand = (mode: ThemeMode) => (mode === "dark" ? "#d92d46" : "#c81e3a");

const components = (mode: ThemeMode): ThemeConfig["components"] => ({
  Button: {
    boxShadow: "none",
    // Not ubc's white: TVMS danger buttons are a light tint (styles/button.css)
    // rather than a solid fill, so the label needs the dark red instead -- and
    // on dark that tint inverts, so the label has to go light with it (2.51:1
    // before, 8.49:1 now).
    dangerColor: mode === "dark" ? "#fda29b" : "#b42318",
  },
  Switch: {
    colorPrimary: brand(mode),
    colorPrimaryHover: brand(mode),
    colorPrimaryActive: brand(mode),
    colorPrimaryBorder: brand(mode),
    colorPrimaryBorderHover: brand(mode),
  },
  Card: {
    colorBgContainer: mode === "dark" ? "#1c1f27" : "#ffffff",
    borderRadius: 8,
    colorBorder: mode === "dark" ? "#262932" : "#EAECF0",
    headerFontSize: 14,
  },
  Form: {
    labelColor: mode === "dark" ? "#d6dbe4" : "#344054",
    labelFontSize: 14,
  },
  Input: {
    colorTextPlaceholder: mode === "dark" ? "#9aa3b2" : "#667085",
    colorIcon: mode === "dark" ? "#9aa3b2" : "#667085",
    colorBorder: mode === "dark" ? "#262932" : "#EAECF0",
    borderRadius: 8,
  },
  Tabs: {
    // The brand red is only 2.90:1 on the dark surface, which made the active
    // tab and ink bar (the main colour on a tabbed page like core/trip) hard to
    // pick out. primary-600 of the dark ramp reaches 5.57:1.
    colorText: mode === "dark" ? "#9aa3b2" : "#667085",
    itemSelectedColor: mode === "dark" ? "#ef6b82" : "#c81e3a",
    inkBarColor: mode === "dark" ? "#ef6b82" : "#c81e3a",
    itemActiveColor: mode === "dark" ? "#ef6b82" : "#c81e3a",
    fontSize: 12,
  },
  Descriptions: {
    labelBg: mode === "dark" ? "#16181f" : "#FAFAFA",
  },
  Alert: {
    colorTextBase: mode === "dark" ? "#d6dbe4" : "#344054",
    colorText: mode === "dark" ? "#d6dbe4" : "#344054",
    colorTextDescription: mode === "dark" ? "#c2c8d4" : "#475467",
  },
  Divider: {
    colorBorder: mode === "dark" ? "#262932" : "#EAECF0",
  },
  Statistic: {
    titleFontSize: 12,
    contentFontSize: 12,
    colorText: mode === "dark" ? "#f2f4f7" : "#101828",
    fontSize: 12,
  },
  Tag: {
    // 1.57:1 on the dark surface before -- effectively unreadable.
    colorText: mode === "dark" ? "#d6dbe4" : "#344054",
    fontSize: 12,
    colorBgContainer: mode === "dark" ? "#262932" : "#F2F4F7",
  },
  Badge: {
    colorBgContainer: mode === "dark" ? "#262932" : "#F2F4F7",
    colorSuccess: mode === "dark" ? "#32d583" : "#027A48",
  },
  Table: {
    // A dark-mode row highlight has to be *lighter* than the surface. #2a1218
    // was darker than #1c1f27, so a hovered row read as a hole punched in the
    // table rather than a highlight.
    rowSelectedBg: mode === "dark" ? "#33232d" : "#fdf1f2",
    rowHoverBg: mode === "dark" ? "#33232d" : "#fdf1f2",
  },
  Segmented: {
    itemSelectedColor: mode === "dark" ? "#f2f4f7" : "#344054",
    trackPadding: 4,
    itemColor: mode === "dark" ? "#9aa3b2" : "#667085",
    fontSize: 12,
    colorTextBase: mode === "dark" ? "#d6dbe4" : "#344054",
  },
});

export const getThemeConfig = (mode: ThemeMode): ThemeConfig => ({
  algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: mode === "dark" ? darkToken : lightToken,
  components: components(mode),
});
